import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import UserNav from '../../components/navbar/user-nav';
import Breadcrumb from '../../components/Breadcrumb';

interface Sugerencia {
  csv: string;
  agencia: string;
  propiedades: number;
  posibleMatch: string | null;
  posibleId: string | null;
  similitud: number;
}

interface PropiedadCSV {
  listNumber: string;
  agenteNombre: string;
  agenciaNombre: string;
}

interface MiembroFirestore {
  id: string;
  nombre: string;
  nombreEnFlex?: string;
  correo: string;
  tipo: 'asociado' | 'asesor';
}

interface SugerenciaConEstado extends Sugerencia {
  idx: number;
  aprobada: boolean;
  nombreFlexFinal: string;
  miembroEncontrado?: MiembroFirestore;
  yaActualizado: boolean;
}

const ActualizarNombresFlex: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();

  const [loading, setLoading] = useState(false);
  const [csvCargado, setCsvCargado] = useState(false);
  const [csvPropiedades, setCsvPropiedades] = useState<PropiedadCSV[]>([]);
  const [sugerencias, setSugerencias] = useState<SugerenciaConEstado[]>([]);
  const [miembrosFirestore, setMiembrosFirestore] = useState<MiembroFirestore[]>([]);
  const [aplicandoCambios, setAplicandoCambios] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [filtro, setFiltro] = useState('');
  const [mostrarSoloSinActualizar, setMostrarSoloSinActualizar] = useState(true);
  const [umbralSimilitud, setUmbralSimilitud] = useState(50);

  const addLog = (msg: string) => {
    setLogs(prev => [...prev, msg]);
    console.log(msg);
  };

  // Normalizar string para comparación
  const normalizar = (str: string): string => {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Parsear línea CSV
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  // Calcular similitud entre dos nombres
  const calcularSimilitud = (nombre1: string, nombre2: string): number => {
    const tokens1 = normalizar(nombre1).split(' ').filter(t => t.length > 1);
    const tokens2 = normalizar(nombre2).split(' ').filter(t => t.length > 1);

    if (tokens1.length === 0 || tokens2.length === 0) return 0;

    let coincidencias = 0;
    tokens1.forEach(t1 => {
      tokens2.forEach(t2 => {
        if (t1 === t2 || (t1.length > 3 && t2.length > 3 && (t1.includes(t2) || t2.includes(t1)))) {
          coincidencias++;
        }
      });
    });

    return coincidencias / Math.max(tokens1.length, tokens2.length);
  };

  // Cargar CSV de propiedades
  const handleCargarCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLogs([]);
    addLog(`📄 Cargando archivo: ${file.name}`);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = parseCSVLine(lines[0]);

      const listNumberIdx = headers.indexOf('List Number');
      const agencyNameIdx = headers.indexOf('Agency Name');
      const listingAgentIdx = headers.indexOf('Listing Agent');
      const statusIdx = headers.indexOf('Status');

      const data: PropiedadCSV[] = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        const values = parseCSVLine(line);

        const status = values[statusIdx] || '';
        if (status !== 'A') continue; // Solo activas

        const agente = values[listingAgentIdx] || '';
        if (agente) {
          data.push({
            listNumber: values[listNumberIdx] || '',
            agenteNombre: agente,
            agenciaNombre: values[agencyNameIdx] || ''
          });
        }
      }

      setCsvPropiedades(data);
      setCsvCargado(true);
      addLog(`✅ ${data.length} propiedades activas cargadas`);

      // Contar agentes únicos
      const agentesUnicos = new Set(data.map(p => p.agenteNombre));
      addLog(`📊 ${agentesUnicos.size} agentes únicos en CSV`);
    };
    reader.readAsText(file);
  };

  // Ejecutar análisis
  const ejecutarAnalisis = async () => {
    if (csvPropiedades.length === 0) {
      alert('Primero carga el CSV de propiedades');
      return;
    }

    setLoading(true);
    setLogs([]);

    try {
      // Cargar miembros de Firestore
      addLog('Cargando miembros de Firestore...');
      const asociadosSnap = await getDocs(collection(db, 'asociado'));
      const asesoresSnap = await getDocs(collection(db, 'asesor'));

      const miembros: MiembroFirestore[] = [
        ...asociadosSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || '',
          nombreEnFlex: d.data().nombreEnFlex || '',
          correo: d.data().correo || '',
          tipo: 'asociado' as const
        })),
        ...asesoresSnap.docs.map(d => ({
          id: d.id,
          nombre: d.data().nombre || '',
          nombreEnFlex: d.data().nombreEnFlex || '',
          correo: d.data().correo || '',
          tipo: 'asesor' as const
        }))
      ];

      setMiembrosFirestore(miembros);
      addLog(`✅ ${miembros.length} miembros cargados de Firestore`);

      // Crear mapa de nombres normalizados
      const miembrosPorNombre = new Map<string, MiembroFirestore>();
      miembros.forEach(m => {
        miembrosPorNombre.set(normalizar(m.nombre), m);
        if (m.nombreEnFlex) {
          miembrosPorNombre.set(normalizar(m.nombreEnFlex), m);
        }
      });

      // Contar propiedades por agente en CSV
      const propsPorAgente = new Map<string, { agencia: string; count: number }>();
      csvPropiedades.forEach(p => {
        if (!propsPorAgente.has(p.agenteNombre)) {
          propsPorAgente.set(p.agenteNombre, { agencia: p.agenciaNombre, count: 0 });
        }
        propsPorAgente.get(p.agenteNombre)!.count++;
      });

      addLog(`📊 Analizando ${propsPorAgente.size} agentes del CSV...`);

      // Buscar matches y sugerencias
      const sugerenciasConEstado: SugerenciaConEstado[] = [];
      let conMatchExacto = 0;
      let idx = 0;

      for (const [agenteCSV, data] of Array.from(propsPorAgente.entries())) {
        const nombreNorm = normalizar(agenteCSV);

        // Buscar match exacto
        const matchExacto = miembrosPorNombre.get(nombreNorm);
        if (matchExacto) {
          conMatchExacto++;
          continue;
        }

        // Buscar mejor match por similitud
        let mejorMatch: MiembroFirestore | undefined = undefined;
        let mejorSimilitud = 0;

        for (const m of miembros) {
          const sim = calcularSimilitud(agenteCSV, m.nombre);
          const simFlex = m.nombreEnFlex ? calcularSimilitud(agenteCSV, m.nombreEnFlex) : 0;
          const maxSim = Math.max(sim, simFlex);

          if (maxSim > mejorSimilitud && maxSim >= 0.4) {
            mejorSimilitud = maxSim;
            mejorMatch = m;
          }
        }

        if (mejorMatch) {
          const yaActualizado = mejorMatch.nombreEnFlex === agenteCSV;

          sugerenciasConEstado.push({
            csv: agenteCSV,
            agencia: data.agencia,
            propiedades: data.count,
            posibleMatch: mejorMatch.nombre,
            posibleId: mejorMatch.id,
            similitud: mejorSimilitud,
            idx: idx++,
            aprobada: false,
            nombreFlexFinal: agenteCSV,
            miembroEncontrado: mejorMatch,
            yaActualizado
          });
        } else {
          // Sin sugerencia
          sugerenciasConEstado.push({
            csv: agenteCSV,
            agencia: data.agencia,
            propiedades: data.count,
            posibleMatch: null,
            posibleId: null,
            similitud: 0,
            idx: idx++,
            aprobada: false,
            nombreFlexFinal: agenteCSV,
            miembroEncontrado: undefined,
            yaActualizado: false
          });
        }
      }

      // Ordenar por propiedades
      sugerenciasConEstado.sort((a, b) => b.propiedades - a.propiedades);

      setSugerencias(sugerenciasConEstado);

      const conSugerencia = sugerenciasConEstado.filter(s => s.posibleMatch).length;
      const yaActualizados = sugerenciasConEstado.filter(s => s.yaActualizado).length;

      addLog('');
      addLog(`📊 RESULTADOS:`);
      addLog(`   ✅ ${conMatchExacto} agentes con match exacto (ya funcionan)`);
      addLog(`   🔗 ${conSugerencia} agentes con sugerencia de match`);
      addLog(`   ❌ ${sugerenciasConEstado.length - conSugerencia} agentes sin sugerencia`);
      addLog(`   ✓ ${yaActualizados} ya tienen nombreEnFlex correcto`);

    } catch (error: any) {
      addLog(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Toggle aprobación
  const toggleAprobacion = (idx: number) => {
    setSugerencias(prev => prev.map(s =>
      s.idx === idx ? { ...s, aprobada: !s.aprobada } : s
    ));
  };

  // Editar nombre final
  const editarNombreFlex = (idx: number, valor: string) => {
    setSugerencias(prev => prev.map(s =>
      s.idx === idx ? { ...s, nombreFlexFinal: valor } : s
    ));
  };

  // Seleccionar todos visibles
  const seleccionarTodosVisibles = () => {
    const visibles = sugerenciasFiltradas.map(s => s.idx);
    setSugerencias(prev => prev.map(s =>
      visibles.includes(s.idx) && !s.yaActualizado ? { ...s, aprobada: true } : s
    ));
  };

  // Deseleccionar todos
  const deseleccionarTodos = () => {
    setSugerencias(prev => prev.map(s => ({ ...s, aprobada: false })));
  };

  // Aplicar cambios
  const aplicarCambios = async () => {
    const aprobadas = sugerencias.filter(s => s.aprobada && s.miembroEncontrado && !s.yaActualizado);

    if (aprobadas.length === 0) {
      alert('No hay sugerencias aprobadas para aplicar');
      return;
    }

    const confirmacion = window.confirm(
      `¿Aplicar cambios a ${aprobadas.length} miembros?\n\nEsto actualizará el campo "nombreEnFlex" en Firestore.`
    );

    if (!confirmacion) return;

    setAplicandoCambios(true);
    setLogs([]);
    addLog(`🚀 Iniciando actualización de ${aprobadas.length} miembros...`);

    let exitosos = 0;
    let errores = 0;

    for (const s of aprobadas) {
      if (!s.miembroEncontrado) continue;

      try {
        const coleccion = s.miembroEncontrado.tipo === 'asociado' ? 'asociado' : 'asesor';
        const docRef = doc(db, coleccion, s.miembroEncontrado.id);

        await updateDoc(docRef, {
          nombreEnFlex: s.nombreFlexFinal
        });

        exitosos++;
        addLog(`✅ ${s.miembroEncontrado.nombre} → nombreEnFlex: "${s.nombreFlexFinal}"`);

        // Marcar como actualizado
        setSugerencias(prev => prev.map(sug =>
          sug.idx === s.idx ? { ...sug, yaActualizado: true, aprobada: false } : sug
        ));

      } catch (error: any) {
        errores++;
        addLog(`❌ Error en ${s.miembroEncontrado.nombre}: ${error.message}`);
      }
    }

    setAplicandoCambios(false);
    addLog('');
    addLog(`📊 RESULTADO: ${exitosos} exitosos, ${errores} errores`);

    if (exitosos > 0) {
      alert(`✅ ${exitosos} miembros actualizados.\n\nAhora puedes ejecutar la asignación de propiedades.`);
    }
  };

  // Filtrar sugerencias
  const sugerenciasFiltradas = sugerencias.filter(s => {
    // Filtro por texto
    if (filtro) {
      const filtroLower = filtro.toLowerCase();
      if (!s.csv.toLowerCase().includes(filtroLower) &&
          !s.posibleMatch?.toLowerCase().includes(filtroLower) &&
          !s.agencia.toLowerCase().includes(filtroLower)) {
        return false;
      }
    }

    // Filtro por similitud
    if (s.similitud * 100 < umbralSimilitud) return false;

    // Filtro por ya actualizado
    if (mostrarSoloSinActualizar && s.yaActualizado) return false;

    return true;
  });

  const totalAprobadas = sugerencias.filter(s => s.aprobada).length;
  const totalYaActualizados = sugerencias.filter(s => s.yaActualizado).length;

  // Verificar permisos
  if (!user?.tipo?.includes('admin')) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h4>Acceso denegado</h4>
          <button className="btn btn-primary" onClick={() => navigate('/admin-dashboard')}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserNav />
      <div className="container-fluid py-4">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Actualizar Nombres Flex' }
          ]}
        />

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-0">
                <i className="fas fa-sync-alt me-2"></i>
                Actualización Masiva de Nombres en Flex
              </h4>
            </div>
            <div className="d-flex gap-2">
              <span className="badge bg-light text-primary">
                {totalYaActualizados} ya actualizados
              </span>
              <span className="badge bg-warning text-dark">
                {sugerencias.length - totalYaActualizados} pendientes
              </span>
            </div>
          </div>

          <div className="card-body">
            {/* Paso 1: Cargar CSV */}
            <div className="card border-primary mb-4">
              <div className="card-header bg-primary text-white">
                <i className="fas fa-file-csv me-2"></i>
                Paso 1: Cargar CSV de Propiedades
              </div>
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCargarCSV}
                      className="form-control"
                    />
                    <small className="text-muted">Archivo: Propiedades - Hoja 1.csv</small>
                  </div>
                  <div className="col-md-3">
                    {csvCargado && (
                      <span className="badge bg-success fs-6">
                        <i className="fas fa-check me-1"></i>
                        {csvPropiedades.length} propiedades
                      </span>
                    )}
                  </div>
                  <div className="col-md-3">
                    <button
                      className="btn btn-primary w-100"
                      onClick={ejecutarAnalisis}
                      disabled={!csvCargado || loading}
                    >
                      {loading ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Analizando...</>
                      ) : (
                        <><i className="fas fa-search me-2"></i>Ejecutar Análisis</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {sugerencias.length > 0 && (
              <>
                {/* Instrucciones */}
                <div className="alert alert-info">
                  <h5><i className="fas fa-info-circle me-2"></i>Paso 2: Revisar y aprobar sugerencias</h5>
                  <ol className="mb-0">
                    <li>Revisa las sugerencias de match entre el CSV y Firestore</li>
                    <li>Marca con ✓ las que son correctas</li>
                    <li>Edita el "Nombre en Flex" si es necesario</li>
                    <li>Haz clic en "Aplicar Cambios"</li>
                    <li>Luego ejecuta la asignación de propiedades</li>
                  </ol>
                </div>

                {/* Filtros y acciones */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="🔍 Buscar por nombre o agencia..."
                      value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={umbralSimilitud}
                      onChange={(e) => setUmbralSimilitud(Number(e.target.value))}
                    >
                      <option value={30}>Similitud ≥ 30%</option>
                      <option value={50}>Similitud ≥ 50%</option>
                      <option value={67}>Similitud ≥ 67%</option>
                      <option value={80}>Similitud ≥ 80%</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check form-switch mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="mostrarSoloSinActualizar"
                        checked={mostrarSoloSinActualizar}
                        onChange={(e) => setMostrarSoloSinActualizar(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="mostrarSoloSinActualizar">
                        Solo pendientes
                      </label>
                    </div>
                  </div>
                  <div className="col-md-3 text-end">
                    <button className="btn btn-outline-primary btn-sm me-2" onClick={seleccionarTodosVisibles}>
                      <i className="fas fa-check-double me-1"></i>Seleccionar visibles
                    </button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={deseleccionarTodos}>
                      Limpiar
                    </button>
                  </div>
                </div>

                {/* Estadísticas */}
                <div className="row mb-3">
                  <div className="col">
                    <span className="text-muted">
                      Mostrando {sugerenciasFiltradas.length} de {sugerencias.length} sugerencias |
                      <strong className="text-success ms-2">{totalAprobadas} seleccionadas para aplicar</strong>
                    </span>
                  </div>
                </div>

                {/* Tabla */}
                <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <table className="table table-sm table-hover">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th style={{ width: '40px' }}>✓</th>
                        <th>Nombre en CSV (Flex)</th>
                        <th>Agencia</th>
                        <th style={{ width: '60px' }}>Props</th>
                        <th>→</th>
                        <th>Miembro en Firestore</th>
                        <th>Nombre en Flex (editable)</th>
                        <th style={{ width: '70px' }}>Similitud</th>
                        <th style={{ width: '80px' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sugerenciasFiltradas.map((s) => (
                        <tr
                          key={s.idx}
                          className={
                            s.yaActualizado ? 'table-success' :
                            s.aprobada ? 'table-warning' : ''
                          }
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={s.aprobada}
                              onChange={() => toggleAprobacion(s.idx)}
                              disabled={s.yaActualizado || !s.miembroEncontrado}
                            />
                          </td>
                          <td>
                            <strong className="text-primary">{s.csv}</strong>
                          </td>
                          <td>
                            <small className="text-muted">{s.agencia}</small>
                          </td>
                          <td>
                            <span className="badge bg-info">{s.propiedades}</span>
                          </td>
                          <td>
                            <i className="fas fa-arrow-right text-muted"></i>
                          </td>
                          <td>
                            {s.miembroEncontrado ? (
                              <>
                                <strong>{s.miembroEncontrado.nombre}</strong>
                                <br />
                                <small className="text-muted">
                                  {s.miembroEncontrado.tipo} |
                                  Actual: {s.miembroEncontrado.nombreEnFlex || '(vacío)'}
                                </small>
                              </>
                            ) : (
                              <span className="text-danger">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                No encontrado: {s.posibleMatch}
                              </span>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={s.nombreFlexFinal}
                              onChange={(e) => editarNombreFlex(s.idx, e.target.value)}
                              disabled={s.yaActualizado || !s.miembroEncontrado}
                            />
                          </td>
                          <td>
                            <span className={`badge ${
                              s.similitud >= 0.8 ? 'bg-success' :
                              s.similitud >= 0.67 ? 'bg-primary' :
                              s.similitud >= 0.5 ? 'bg-warning text-dark' :
                              'bg-secondary'
                            }`}>
                              {(s.similitud * 100).toFixed(0)}%
                            </span>
                          </td>
                          <td>
                            {s.yaActualizado ? (
                              <span className="badge bg-success">
                                <i className="fas fa-check me-1"></i>OK
                              </span>
                            ) : !s.miembroEncontrado ? (
                              <span className="badge bg-danger">
                                <i className="fas fa-times me-1"></i>Error
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">Pendiente</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Botón aplicar */}
                <div className="mt-4 d-flex justify-content-between align-items-center">
                  <div>
                    <a href="/admin-diagnostico-propiedades" className="btn btn-outline-secondary">
                      <i className="fas fa-stethoscope me-2"></i>Ir a Diagnóstico
                    </a>
                  </div>
                  <button
                    className="btn btn-success btn-lg"
                    onClick={aplicarCambios}
                    disabled={aplicandoCambios || totalAprobadas === 0}
                  >
                    {aplicandoCambios ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Aplicando...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Aplicar Cambios ({totalAprobadas})
                      </>
                    )}
                  </button>
                </div>

              </>
            )}

            {/* Logs - siempre visible */}
            {logs.length > 0 && (
              <div className="card mt-4">
                <div className="card-header">
                  <i className="fas fa-terminal me-2"></i>Registro de Operaciones
                </div>
                <div className="card-body bg-dark text-light" style={{ maxHeight: '300px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {logs.map((log, idx) => (
                    <div key={idx} className={
                      log.includes('❌') ? 'text-danger' :
                      log.includes('✅') ? 'text-success' :
                      log.includes('📊') ? 'text-info fw-bold' :
                      log.includes('🚀') ? 'text-warning fw-bold' :
                      log.includes('📄') ? 'text-warning' :
                      'text-light'
                    }>
                      {log}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ActualizarNombresFlex;
